import { z } from "zod";
import { NodeKinds } from "interfaces/enums";

const OrderCodeObject = z.object({
  seriesId: z.number().optional(),
  currentStrategyId: z.string().optional(),
  state: z.record(z.string(), z.unknown()),
  propertyStates: z.array(z.unknown()).optional(),
});

const BaseNodeSchema = z.object({
  id: z.uuid({ message: "Invalid UUID" }),
  parentId: z.union([z.uuid({ message: "Invalid UUID" }), z.null()]),
  position: z.number({ message: "Invalid reference position" }),
  active: z.boolean().default(true),
  partiallyVisible: z.boolean().default(false),
});

const BaseFolderSchema = BaseNodeSchema.extend({
  folderName: z.string().min(1, {
    message: "Folder name is too short, should be at least 1 character long",
  }),
  folderDescription: z.string().nullable(),
  nodes: z.array(z.any()).default([]),
});

const SelectedAccessoryItem = z.object({
  accessoryId: z.string(),
  accessoryGroupId: z.string(),
  seriesId: z.number(),
  currentPresentation: z.string(),
  optionStates: z.array(z.unknown()).nullable(),
});

const BaseProductSchema = BaseNodeSchema.extend({
  productName: z.string().min(1, { message: "ProductName is required" }),
  productDescription: z.string().nullable(),
  orderCode: OrderCodeObject.nullable(),
  pimId: z.string().nullable(),
  presentation: z.string().min(2, { message: "Presentation is required" }),
  quantity: z.number().min(1, { message: "Quantity is required" }),
  nodes: z.array(z.any()).nullable().default(null),
  price: z.number().nullable().default(null),
  priceGroup: z.string().nullable().default(null),
  selectedAccessories: z.array(SelectedAccessoryItem).nullable(),
});

export const RootSchema = BaseFolderSchema.extend({
  kind: z.literal(NodeKinds.ROOT),
});

export const FolderSchema = BaseFolderSchema.extend({
  kind: z.literal(NodeKinds.FOLDER),
});

export const WizardFolderSchema = BaseFolderSchema.extend({
  kind: z.literal(NodeKinds.WIZARD_FOLDER),
});

export const WizardRootFolderSchema = BaseFolderSchema.extend({
  kind: z.literal(NodeKinds.WIZARD_ROOT_FOLDER),
});

export const ProductSchema = BaseProductSchema.extend({
  kind: z.literal(NodeKinds.PRODUCT),
});

export const WizardProductSchema = BaseProductSchema.extend({
  kind: z.literal(NodeKinds.WIZARD_PRODUCT),
  presentation: z.string().nullable().default(null),
});

export const WizardCommissionCostSchema = BaseProductSchema.extend({
  kind: z.literal(NodeKinds.WIZARD_COMMISIONING_COST),
  presentation: z.string().nullable().default(null),
});

export const TroxNetcomFolderSchema = BaseFolderSchema.extend({
  kind: z.literal(NodeKinds.TROX_NETCOM_FOLDER),
});

export const TroxNetcomProductSchema = BaseProductSchema.extend({
  kind: z.literal(NodeKinds.TROX_NETCOM_PRODUCT),
  presentation: z.string().nullable().default(null),
});

export const TroxNetcomFolderRootSchema = BaseFolderSchema.extend({
  kind: z.literal(NodeKinds.TROX_NETCOM_FOLDER_ROOT),
});

export const TroxNetcomGenericProductSchema = BaseProductSchema.extend({
  kind: z.literal(NodeKinds.TROX_NETCOM_GENERIC_PRODUCT),
  presentation: z.string().nullable().default(null),
});

export const TroxNetcomNoteSchema = BaseProductSchema.extend({
  kind: z.literal(NodeKinds.TROX_NETCOM_NOTE),
  presentation: z.string().nullable().default(null),
});

export const CustomProductSchema = BaseProductSchema.extend({
  kind: z.literal(NodeKinds.CUSTOM_PRODUCT),
  presentation: z.string().nullable().default(null),
}).omit({ orderCode: true });

export const ProductAccessorySchema = BaseProductSchema.extend({
  id: z.string(),
  kind: z.literal(NodeKinds.PRODUCT_ACCESSORY),
  accessoryId: z.string(),
  accessoryGroupId: z.string(),
});

export const WizardProductAccessorySchema = BaseProductSchema.extend({
  id: z.string(),
  kind: z.literal(NodeKinds.WIZARD_PRODUCT_ACCESSORY),
  accessoryId: z.string(),
  accessoryGroupId: z.string(),
});

export const NodeSchema = z.discriminatedUnion("kind", [
  FolderSchema,
  WizardFolderSchema,
  WizardRootFolderSchema,
  ProductSchema,
  WizardProductSchema,
  RootSchema,
  TroxNetcomFolderSchema,
  TroxNetcomProductSchema,
  TroxNetcomFolderRootSchema,
  TroxNetcomGenericProductSchema,
  CustomProductSchema,
  WizardCommissionCostSchema,
  TroxNetcomNoteSchema,
  ProductAccessorySchema,
  WizardProductAccessorySchema,
]);

export type Node = z.infer<typeof NodeSchema>;
export type Product = z.infer<typeof ProductSchema>;
export type WizardProduct = z.infer<typeof WizardProductSchema>;
export type Folder = z.infer<typeof FolderSchema>;
export type WizardFolder = z.infer<typeof WizardFolderSchema>;
export type WizardRootFolder = z.infer<typeof WizardRootFolderSchema>;
export type Root = z.infer<typeof RootSchema>;
export type TroxNetcomFolder = z.infer<typeof TroxNetcomFolderSchema>;
export type TroxNetcomProduct = z.infer<typeof TroxNetcomProductSchema>;
export type TroxNetcomFolderRoot = z.infer<typeof TroxNetcomFolderRootSchema>;
export type TroxNetcomGenericProduct = z.infer<
  typeof TroxNetcomGenericProductSchema
>;
export type TroxNetcomNote = z.infer<typeof TroxNetcomNoteSchema>;
export type CustomProduct = z.infer<typeof CustomProductSchema>;
export type WizardCommissionCost = z.infer<typeof WizardCommissionCostSchema>;
export type ProductAccessory = z.infer<typeof ProductAccessorySchema>;
export type WizardProductAccessory = z.infer<
  typeof WizardProductAccessorySchema
>;

export type AnyFolder =
  | Folder
  | WizardFolder
  | WizardRootFolder
  | TroxNetcomFolder
  | TroxNetcomFolderRoot;

export type AnyProduct =
  | Product
  | WizardProduct
  | TroxNetcomProduct
  | TroxNetcomGenericProduct
  | CustomProduct
  | WizardCommissionCost
  | TroxNetcomNote
  | ProductAccessory
  | WizardProductAccessory;

export const UpdateProductSchema = ProductSchema.pick({
  id: true,
  kind: true,
}).extend(
  ProductSchema.omit({
    id: true,
    kind: true,
    nodes: true,
    partiallyVisible: true,
  }).partial().shape,
);

export const UpdateAccessorySchema = ProductAccessorySchema.pick({
  id: true,
  kind: true,
}).extend(
  ProductAccessorySchema.omit({
    id: true,
    kind: true,
    nodes: true,
    partiallyVisible: true,
  }).partial().shape,
);

export const UpdateCustomProductSchema = CustomProductSchema.pick({
  id: true,
  kind: true,
}).extend(
  CustomProductSchema.omit({
    id: true,
    kind: true,
    pimId: true,
    nodes: true,
    partiallyVisible: true,
  }).partial().shape,
);

export const UpdateFolderSchema = FolderSchema.pick({
  id: true,
  kind: true,
}).extend(
  FolderSchema.omit({
    id: true,
    kind: true,
    nodes: true,
    partiallyVisible: true,
  }).partial().shape,
);

export const UpdateNodeSchema = z.discriminatedUnion("kind", [
  UpdateProductSchema,
  UpdateFolderSchema,
  UpdateCustomProductSchema,
  UpdateAccessorySchema,
]);

export type UpdateNode = z.infer<typeof UpdateNodeSchema>;
